import React, { useState } from 'react'
import { CheckIcon } from '@heroicons/react/24/outline'

/**
 * Umowa i montaż po wyborze klienta (prośba właściciela 2026-11-03, backend `rynek/realizacja.py`): statystyka, czy
 * z Rynku wyszła instalacja. Potwierdza firma albo klient; montaż odhaczony w kalendarzu karty klienta w CRM firmy liczy
 * się sam. Od wyboru do montażu mija często miesiąc i więcej, więc to cicha sekcja na dole strony zlecenia.
 *
 * `stan`: {etap: {data, kto}}, `strona`: 'firma' | 'klient', `onZapisz(dane)` zwraca odpowiedź API ({ok, error}).
 */
const ETAPY = [['umowa', 'Podpisana umowa'], ['montaz', 'Montaż zakończony'], ['bez_umowy', 'Nie doszło do umowy']]
const KTO = {
  firma: { firma: 'potwierdziła Twoja firma', klient: 'potwierdził klient', kalendarz: 'z kalendarza w karcie klienta' },
  klient: { firma: 'potwierdził wykonawca', klient: 'potwierdziłeś Ty' },
}
const dzien = (iso) => (iso ? iso.split('-').reverse().join('.') : '')
const dzisIso = () => {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

const RealizacjaZlecenia = ({ stan, strona, onZapisz, tytul = 'Umowa i montaż' }) => {
  const [wybrany, setWybrany] = useState(null)
  const [data, setData] = useState(dzisIso())
  const [busy, setBusy] = useState(false)
  const [blad, setBlad] = useState('')
  const s = stan || {}

  const zapisz = async (dane) => {
    setBusy(true); setBlad('')
    const r = await onZapisz(dane)
    setBusy(false)
    if (r && !r.ok) { setBlad(r.error); return }
    setWybrany(null)
  }

  const potwierdzone = ETAPY.filter(([k]) => s[k])
  const doWyboru = ETAPY.filter(([k]) => !s[k] && !(k !== 'bez_umowy' && s.bez_umowy))

  return (
    <section aria-label={tytul} className="rounded-[18px] border border-gray-200 bg-white p-4 text-sm sm:p-5">
      <h2 className="m-0 text-[15px] font-semibold text-gray-900">{tytul}</h2>
      <p className="m-0 mt-1 text-[13px] leading-5 text-gray-600">
        {strona === 'firma'
          ? 'Daj znać, gdy podpiszecie umowę i po montażu. Montaż zakończony w kalendarzu karty klienta odhaczy się sam. Klient widzi Twoje potwierdzenia.'
          : 'Daj znać, gdy podpiszesz umowę z wykonawcą i po montażu. Wykonawca widzi Twoje potwierdzenia.'}
      </p>
      {potwierdzone.length > 0 && (
        <ul className="m-0 mt-3 flex list-none flex-col gap-1.5 p-0">
          {potwierdzone.map(([k, nazwa]) => (
            <li key={k} className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-gray-800">
              <CheckIcon className={`h-4 w-4 flex-none ${k === 'bez_umowy' ? 'text-gray-500' : 'text-emerald-600'}`} strokeWidth={2.4} aria-hidden="true" />
              <span className="font-semibold">{nazwa}</span>
              <span className="text-gray-500">{[dzien(s[k].data), KTO[strona][s[k].kto]].filter(Boolean).join(', ')}</span>
              {/* Cofnąć można tylko własne potwierdzenie; montaż z kalendarza cofa się w karcie klienta. */}
              {s[k].kto === strona && (
                <button type="button" disabled={busy} onClick={() => zapisz({ etap: k, cofnij: true })}
                        className="text-[13px] font-semibold text-gray-500 hover:text-gray-800">Cofnij</button>
              )}
            </li>
          ))}
        </ul>
      )}
      {doWyboru.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {doWyboru.map(([k, nazwa]) => (
            <button key={k} type="button" disabled={busy} aria-pressed={wybrany === k}
                    onClick={() => (k === 'bez_umowy' ? zapisz({ etap: k }) : setWybrany(wybrany === k ? null : k))}
                    className={`min-h-[36px] rounded-full border-[1.5px] px-3 text-[13px] font-medium transition
                      ${wybrany === k ? 'border-blue-600 bg-blue-600 text-white' : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'}`}>
              {nazwa}
            </button>
          ))}
        </div>
      )}
      {wybrany && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <label className="flex items-center gap-2 text-[13px] text-gray-700">Kiedy?
            <input type="date" value={data} max={dzisIso()} onChange={(e) => setData(e.target.value)} aria-label="Data"
                   className="h-[36px] rounded-lg border-[1.5px] border-gray-300 bg-white px-2 text-[13px] text-gray-900 focus:border-blue-600 focus:outline-none" />
          </label>
          <button type="button" disabled={busy || !data} onClick={() => zapisz({ etap: wybrany, data })}
                  className="h-[36px] rounded-lg bg-blue-600 px-3 text-[13px] font-semibold text-white hover:bg-blue-700 disabled:opacity-60">
            Zapisz
          </button>
        </div>
      )}
      {blad && <p role="alert" className="m-0 mt-2 text-[13px] text-red-700">{blad}</p>}
    </section>
  )
}

export default RealizacjaZlecenia
